/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Asset, EntryPoint, Folder } from '@wit/vtv-x-foundation-lib';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import MainSection from 'types/MainSection';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import { useLocationWithState } from 'hooks/useLocationWithState';
import { ActionCreators, FolderInfoContext, Selectors } from 'store/ducks/folderInfo';
import { StoreState } from 'store/store';
import { SimpleHeader, Spinner } from 'ui/components';
import Constants from 'configs/Constants';
import { PrivateRoutePath } from 'routes/paths';
import useIsOnViewportObserver from 'hooks/useIsOnViewportObserver';
import Grid from '../../components/Grid';
import { useRouter } from '../../../hooks/useRouter';

interface PathParams {
  assetId: string;
  channelId: string;
  entryPoint?: EntryPoint;
}

interface LocationState {
  entryFolder: Folder;
}

export default function FolderGrid(): JSX.Element | null {
  const [assetsPageIndex, setAssetsPageIndex] = useState(0);
  const location = useLocationWithState();
  const history = useHistoryWithState();
  const router = useRouter();

  const { assetId, channelId } = useParams<PathParams>();
  const { entryPoint } = router.queryParams;
  const { entryFolder } = (location.state as LocationState | undefined) || {};

  const channelIdNumber = Number(channelId);

  const dispatch = useDispatch();

  const ctxRef = useRef<FolderInfoContext>({
    entryAsset: entryFolder,
    entryAssetId: entryFolder?.id || Number(assetId),
    entryPoint,
  });

  const rail = useSelector((store: StoreState) => Selectors.rails.getRail(store, ctxRef.current, channelIdNumber));
  const asset = useSelector((state: StoreState) => Selectors.folder.getAsset(state, ctxRef.current));
  const isAssetProcessing = useSelector((state: StoreState) => Selectors.folder.isProcessing(state, ctxRef.current));
  const assetError = useSelector((state: StoreState) => Selectors.folder.error(state, ctxRef.current));

  const folder = useRef(entryFolder || asset);

  useEffect(() => {
    const ctx = ctxRef.current;

    if (!entryFolder) {
      dispatch(ActionCreators.init(ctx, undefined, channelIdNumber));
    }

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const ctx = ctxRef.current;
    dispatch(ActionCreators.rails.loadMoreAssets(ctx, channelIdNumber, assetsPageIndex));
  }, [assetsPageIndex, channelIdNumber, dispatch]);

  useEffect(() => {
    if (!assetError && !Number.isNaN(channelIdNumber)) {
      return;
    }

    const section = router.getSection();

    switch (section) {
      case MainSection.MY_TV:
        history.replace(PrivateRoutePath.MYTV);
        break;
      case MainSection.WATCH_TV:
        history.replace(PrivateRoutePath.WATCHTV);
        break;
      default:
        history.replace(PrivateRoutePath.ONDEMAND);
    }
  }, [assetError, channelIdNumber, history, router]);

  const existsMoreAssetsToLoad = (rail?.assets || []).length % Constants.ASSETS_PER_GRID_PAGE === 0;

  // #region INFINITE SCROLL
  const gridRef = useRef(null);
  const isOnViewport = useIsOnViewportObserver(gridRef, {
    root: null,
    rootMargin: '0px',
    threshold: [0.9, 1],
  });

  useEffect(() => {
    if (isOnViewport && !rail?.loadingAssets && existsMoreAssetsToLoad) {
      setAssetsPageIndex((prevState) => prevState + 1);
    }
  }, [dispatch, existsMoreAssetsToLoad, isOnViewport, rail?.loadingAssets]);
  // #endregion INFINITE SCROLL

  const onBack = useCallback(() => {
    if (folder.current) {
      router.push({ asset: folder.current, entryPoint });
    }
  }, [entryPoint, router]);

  if (isAssetProcessing) {
    return (
      <div className="layout-spinner-container">
        <Spinner isActive />
      </div>
    );
  }

  const onCardClick = (assetClicked: Asset) => {
    router.push({ asset: assetClicked, entryPoint });
  };

  return (
    <div id="FolderGrid_container" className="ondemand-nextlevel ondemand-grid template-holder back-nav">
      <SimpleHeader subtitle={rail?.title || ''} backLabel={folder.current?.title} onBack={onBack} hideBackIcon />

      <Grid
        className="no-background"
        railId="folderGrid"
        assets={rail?.assets}
        // menuItemId => don't send menu item here since the folders menu item is not part of the original menu
        onCardClick={onCardClick}
      />
      <div
        id="FolderGrid_buttonContainer"
        className="load-more-btn-container margin-top-20 margin-bottom-66"
        ref={gridRef}
      >
        {rail?.loadingAssets ? (
          <div className="layout-spinner-container">
            <Spinner isActive />
          </div>
        ) : null}
      </div>
    </div>
  );
}
